import React, { useEffect, useState } from "react";
import { Button, Input } from '@scuf/common';
import './GatewayAssociationTabContent.scss';
import { TreeNode } from "@bmsp/bmsp.web.component.common";

import toasts from "../../../commonUtils/toasts";
import i18next from "i18next";
import GatewayListTable from "./GatewayListTable";
import { useAuthService } from "../../../../auth/hooks/useAuthService";


interface IGatewayAssociationtabContent{
    selectedNode: TreeNode;
    hasGatewayUpdatePerm: boolean;
    enableGatewayTableList: boolean;
}

interface IAssociationResponse {
    Message: {
        MessageDetails: string;
        StatusCode: number;
    }
}

const GatewatAssociationTabContent = (props: IGatewayAssociationtabContent) => {
    const { authFetch, baseURL } = useAuthService();
    const [enableAssociateGateway, setEnableAssociateGateway] = useState(false);
    const [enableAssociateAndSyncBtn, setEnableAssociateAndSyncBtn] = useState(false);
    const [enteredGuid, setEnteredGuid] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>();
    const [loader, setLoader] = useState<boolean>(false);

    const [listloader, setListLoader] = useState<boolean>(false);
    const [data, setData] = useState<any[]>([]);

    const getStatus = (siteId: string) => {
        return authFetch(
            `${baseURL}/api/OnboardingTracker/${siteId}/status/modelsync`,
            {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
            }
        ).then((response) => response.json().then((data) => data)).catch(() => null);
    }

    const gatewayList = (orgId: string, siteId: string) => {
        setListLoader(true);
        return authFetch(
            `${baseURL}/api/GatewayRegistration/org/${orgId}/site/${siteId}/gateways`,
            {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
            }
        ).then((response) => {
            response.json().then((data) => {
                const finalData = data.map((item: any) => {
                    return {
                        key: item.systemGuid,
                        data: {
                            Id: item.systemGuid,
                            Type: item.gatewayType,
                            status: null,
                            action: null
                        }
                    }
                });
                setData(finalData);
                const status = getStatus(siteId);
                status.then((statusData) => {
                    const updated = finalData.map((item: any) => {
                        const statusDataItem = statusData.find((statusItem: any) => statusItem.targetid === item.key);
                        return {
                            ...item,
                            data: {
                                ...item.data,
                                status: {
                                    status: statusDataItem ? statusDataItem.status : null,
                                    failureReason: statusDataItem && statusDataItem.status === "failed" ? statusDataItem.metadata.reason : null
                                },
                                action: {
                                    type: item.data.Type,
                                    status: statusDataItem ? statusDataItem.status : null,
                                }
                            }
                        }
                    });
                    setData(updated);
                }).catch(() => {
                    const erroredData = finalData.map((item: any) => {
                        return {
                            ...item,
                            data: {
                                ...item.data,
                                status: {
                                    status: "API failed"
                                },
                                action: {
                                    status: "API failed"
                                }
                            }
                        }
                    });
                    setData(erroredData);
                });                
            });
        }).catch(() => {
            return null;
        }).finally(() => {
            setListLoader(false);
        });
    };

    useEffect(() => {
        if(props.enableGatewayTableList)
            gatewayList(props.selectedNode.data.rootNodeId, props.selectedNode.data.id);
    }, []);
    
    const triggerGatewayAssociation = (data: any) => {
        setEnableAssociateAndSyncBtn(false);
        setLoader(true);
        authFetch(
            `${baseURL}/api/GatewayRegistration/Association`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                SystemGuid: enteredGuid,
                SiteId: data.id,
                OrgId: data.rootNodeId,
                SiteName: data.name,
                IsForgeSite: true 
                })
            }
        ).then((response) => {
            if(response.ok){
                response.json().then((data: IAssociationResponse) => {
                    if(data.Message.StatusCode > 200){
                        toasts.warn("Error in initiating cloud sync");
                        toasts.success(i18next.t('successMessage1'));
                    }else{
                        toasts.success(i18next.t('successMessage2'));
                    }
                    if(props.enableGatewayTableList)
                        gatewayList(props.selectedNode.data.rootNodeId, props.selectedNode.data.id);
                });
            }else{
                toasts.error(i18next.t('errorMessage'))
            }
        }).catch(() => {
            toasts.error(i18next.t('errorMessage'));
        }).finally(() => {
            setLoader(false);
            setEnteredGuid('');
        });
    }
    
    const isValidGuid = (guid: string) => {
        const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
        return regex.test(guid);
    }

    return (
        <div className="gateway-main-section">
            {props.hasGatewayUpdatePerm ? 
            !enableAssociateGateway ? 
                <div className="add-gateway-btn">
                    <Button
                        type="secondary"
                        size="medium"
                        className="gateway-btn"
                        onClick={() => setEnableAssociateGateway(true)}
                        content={i18next.t('addNewGateway')}
                        />
                </div> : (
                    <div className="gateway-add-section">
                        <Input
                            className="gateway-input"
                            value={enteredGuid}
                            placeholder={i18next.t('gatewayId')}
                            onChange={(value: string) => {
                                if(!isValidGuid(value)) {
                                    setErrorMessage(i18next.t('guidValidationError'));
                                    setEnableAssociateAndSyncBtn(false);
                                }else if(data.length > 0 && data.map((item) => item.key).includes(value)){
                                    setErrorMessage("GUID already associated");
                                    setEnableAssociateAndSyncBtn(false);
                                }
                                else{
                                    setEnableAssociateAndSyncBtn(true);
                                    setErrorMessage('');
                                };
                                setEnteredGuid(value);
                            }}
                        />
                        {errorMessage && <p className="gateway-error-msg">{errorMessage}</p>}
                        <Input
                            className="gateway-input"
                            value="EBI"
                            disabled={true}
                        />
                        <div className="submit-gateway">
                            <Button
                                type="secondary"
                                className="gateway-btn"
                                size="medium"
                                onClick={() => {setEnableAssociateGateway(false); setEnteredGuid(''); setErrorMessage('')}}
                                content={i18next.t('cancelBtn')}
                                disabled={loader}
                            />
                            <Button
                                type="primary"
                                className="gateway-btn content-color"
                                size="medium"
                                onClick={() => triggerGatewayAssociation(props.selectedNode.data)}
                                disabled={!enableAssociateAndSyncBtn}
                                loading={loader}
                                content={i18next.t('associateAndSyncBtn')}
                            />
                        </div>
                    </div> 
                )
            : null           
            }
            {props.enableGatewayTableList ? <GatewayListTable data={data} loader={listloader} onReSync={triggerGatewayAssociation} basedata={props.selectedNode.data}/> : null}
        </div>
    );
}

export default GatewatAssociationTabContent;